import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, Video, Clock, DollarSign, Languages, Award, Filter, X, ChevronDown } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { doctors, specializations } from '../mockData';

const FindDoctors = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedAvailability, setSelectedAvailability] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [selectedExperience, setSelectedExperience] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedInsurance, setSelectedInsurance] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Filter doctors based on all criteria
  const filteredDoctors = useMemo(() => {
    return doctors.filter(doctor => {
      const matchesSearch = searchQuery === '' || 
        doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doctor.specialty.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesSpecialty = selectedSpecialty === '' || doctor.specialty === selectedSpecialty;
      const matchesLocation = selectedLocation === '' || doctor.clinicAddress.toLowerCase().includes(selectedLocation.toLowerCase());
      const matchesAvailability = selectedAvailability === '' || doctor.nextAvailable.toLowerCase().includes(selectedAvailability.toLowerCase());
      const matchesRating = doctor.rating >= minRating;
      
      const matchesExperience = selectedExperience === '' || 
        (selectedExperience === '5-10' && doctor.experience >= 5 && doctor.experience <= 10) ||
        (selectedExperience === '10-15' && doctor.experience > 10 && doctor.experience <= 15) ||
        (selectedExperience === '15+' && doctor.experience > 15);
      
      const matchesGender = selectedGender === '' || doctor.gender === selectedGender;
      const matchesLanguage = selectedLanguage === '' || doctor.languages.includes(selectedLanguage);
      const matchesInsurance = selectedInsurance === '' || doctor.acceptsInsurance.includes(selectedInsurance);
      
      return matchesSearch && matchesSpecialty && matchesLocation && matchesAvailability && 
             matchesRating && matchesExperience && matchesGender && matchesLanguage && matchesInsurance;
    });
  }, [searchQuery, selectedSpecialty, selectedLocation, selectedAvailability, minRating, 
      selectedExperience, selectedGender, selectedLanguage, selectedInsurance]);

  const featuredDoctors = doctors.filter(d => d.featured);
  const topRatedDoctors = [...doctors].sort((a, b) => b.rating - a.rating).slice(0, 4);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedSpecialty('');
    setSelectedLocation('');
    setSelectedAvailability('');
    setMinRating(0);
    setSelectedExperience('');
    setSelectedGender('');
    setSelectedLanguage('');
    setSelectedInsurance('');
  };

  const DoctorCard = ({ doctor }) => (
    <div 
      onClick={() => navigate(`/doctor/${doctor.id}`)}
      style={styles.doctorCard}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 20px 60px rgba(29, 87, 213, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
      }}
    >
      {doctor.featured && (
        <div style={styles.featuredBadge}>
          <Award size={14} />
          <span>Featured</span>
        </div>
      )}
      
      <div style={styles.cardHeader}>
        <img src={doctor.photo} alt={doctor.name} style={styles.doctorPhoto} />
        <div style={styles.ratingBadge}>
          <Star size={14} fill="#FFD700" color="#FFD700" />
          <span>{doctor.rating}</span>
        </div>
      </div>

      <div style={styles.cardContent}>
        <h3 style={styles.doctorName}>{doctor.name}</h3>
        <p style={styles.specialty}>{doctor.specialty}</p>
        <p style={styles.qualifications}>{doctor.qualifications}</p>

        <div style={styles.infoRow}>
          <div style={styles.infoItem}>
            <Award size={16} color="#1D57D5" />
            <span>{doctor.experience} years exp.</span>
          </div>
          <div style={styles.infoItem}>
            <Star size={16} color="#FFD700" />
            <span>{doctor.reviewCount} reviews</span>
          </div>
        </div>

        <div style={styles.infoRow}>
          <div style={styles.infoItem}>
            <MapPin size={16} color="#28C08A" />
            <span>{doctor.distance}</span>
          </div>
          <div style={styles.infoItem}>
            <DollarSign size={16} color="#1D57D5" />
            <span>${doctor.consultationFee}</span>
          </div>
        </div>

        <div style={styles.availabilityRow}>
          <div style={styles.nextAvailable}>
            <Clock size={14} />
            <span>Next: {doctor.nextAvailable}</span>
          </div>
          {doctor.videoConsultation && (
            <div style={styles.videoTag}>
              <Video size={14} />
              <span>Video</span>
            </div>
          )}
        </div>

        <button style={styles.bookButton}>
          Book Appointment
        </button>
      </div>
    </div>
  );

  const responsiveStyles = getResponsiveStyles(isMobile, isSmallMobile);

  return (
    <div style={responsiveStyles.pageWrapper}>
      <Navbar />
      
      {/* Hero Section */}
      <section style={responsiveStyles.hero}>
        <div style={responsiveStyles.heroContent}>
          <div style={responsiveStyles.badge}>
            <Search size={isSmallMobile ? 14 : 16} />
            <span>Find Your Doctor</span>
          </div>
          <h1 style={responsiveStyles.heroTitle}>
            Search for <span style={responsiveStyles.gradientText}>Verified Doctors</span>
          </h1>
          <p style={responsiveStyles.heroSubtitle}>
            Book appointments with top-rated healthcare professionals near you
          </p>

          {/* Search Bar */}
          <div style={responsiveStyles.searchContainer}>
            <div style={responsiveStyles.searchBox}>
              <Search size={isSmallMobile ? 18 : 20} color="#64748B" />
              <input
                type="text"
                placeholder="Search by doctor name or specialty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={responsiveStyles.searchInput}
              />
            </div>
            <button 
              style={responsiveStyles.filterToggleBtn}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={isSmallMobile ? 18 : 20} />
              <span>Filters</span>
            </button>
          </div>

          {/* Stats */}
          <div style={responsiveStyles.statsRow}>
            <div style={responsiveStyles.statItem}>
              <span style={responsiveStyles.statNumber}>{doctors.length}+</span>
              <span style={responsiveStyles.statLabel}>Doctors</span>
            </div>
            <div style={responsiveStyles.statDivider}></div>
            <div style={responsiveStyles.statItem}>
              <span style={responsiveStyles.statNumber}>12</span>
              <span style={responsiveStyles.statLabel}>Specialties</span>
            </div>
            <div style={responsiveStyles.statDivider}></div>
            <div style={responsiveStyles.statItem}>
              <span style={responsiveStyles.statNumber}>100%</span>
              <span style={responsiveStyles.statLabel}>Verified</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Doctors Section */}
      <section style={responsiveStyles.sectionWrapper}>
        <div style={responsiveStyles.container}>
          <div style={responsiveStyles.sectionHeader}>
            <Award size={isSmallMobile ? 24 : isMobile ? 26 : 28} color="#F4CE7B" />
            <h2 style={responsiveStyles.sectionTitle}>Featured Doctors</h2>
          </div>
          <div style={responsiveStyles.doctorsGrid}>
            {featuredDoctors.map(doctor => (
              <DoctorCard key={doctor.id} doctor={doctor} />
            ))}
          </div>
        </div>
      </section>

      {/* Top Rated Doctors Section */}
      <section style={responsiveStyles.sectionWrapper}>
        <div style={responsiveStyles.container}>
          <div style={responsiveStyles.sectionHeader}>
            <Star size={isSmallMobile ? 24 : isMobile ? 26 : 28} color="#FFD700" />
            <h2 style={responsiveStyles.sectionTitle}>Top Rated Doctors</h2>
          </div>
          <div style={responsiveStyles.doctorsGrid}>
            {topRatedDoctors.map(doctor => (
              <DoctorCard key={doctor.id} doctor={doctor} />
            ))}
          </div>
        </div>
      </section>

      {/* Main Content: Filters + Results */}
      <section style={responsiveStyles.mainSection}>
        {isMobile && showFilters && (
          <div 
            style={responsiveStyles.backdrop}
            onClick={() => setShowFilters(false)}
          />
        )}
        <div style={responsiveStyles.container}>
          <div style={responsiveStyles.mainGrid}>
            {/* Filters Sidebar */}
            <div style={{
              ...responsiveStyles.filtersSidebar,
              ...(isMobile && showFilters ? responsiveStyles.filtersSidebarMobile : {}),
              display: showFilters || !isMobile ? 'block' : 'none'
            }}>
              {isMobile && (
                <div style={responsiveStyles.mobileFilterHeader}>
                  <h3 style={responsiveStyles.filtersTitle}>Filters</h3>
                  <button onClick={() => setShowFilters(false)} style={responsiveStyles.closeFilterBtn}>
                    <X size={20} />
                  </button>
                </div>
              )}
              {!isMobile && (
                <div style={responsiveStyles.filtersHeader}>
                  <h3 style={responsiveStyles.filtersTitle}>Filters</h3>
                  <button onClick={clearFilters} style={responsiveStyles.clearBtn}>
                    <X size={16} />
                    Clear All
                  </button>
                </div>
              )}
              {isMobile && (
                <button onClick={clearFilters} style={{...responsiveStyles.clearBtn, width: '100%', marginBottom: '24px'}}>
                  <X size={16} />
                  Clear All
                </button>
              )}

              {/* Specialty Filter */}
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Specialty</label>
                <select 
                  value={selectedSpecialty} 
                  onChange={(e) => setSelectedSpecialty(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="">All Specialties</option>
                  {specializations.map(spec => (
                    <option key={spec.id} value={spec.name}>{spec.name}</option>
                  ))}
                </select>
              </div>

              {/* Location Filter */}
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Location</label>
                <input
                  type="text"
                  placeholder="Enter area or zip code"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  style={styles.filterInput}
                />
              </div>

              {/* Availability Filter */}
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Availability</label>
                <select 
                  value={selectedAvailability} 
                  onChange={(e) => setSelectedAvailability(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="">Any time</option>
                  <option value="today">Today</option>
                  <option value="tomorrow">Tomorrow</option>
                  <option value="this week">This week</option>
                </select>
              </div>

              {/* Rating Filter */}
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Minimum Rating</label>
                <select 
                  value={minRating} 
                  onChange={(e) => setMinRating(Number(e.target.value))}
                  style={styles.filterSelect}
                >
                  <option value="0">Any rating</option>
                  <option value="4">4+ Stars</option>
                  <option value="4.5">4.5+ Stars</option>
                  <option value="4.8">4.8+ Stars</option>
                </select>
              </div>

              {/* Experience Filter */}
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Experience</label>
                <select 
                  value={selectedExperience} 
                  onChange={(e) => setSelectedExperience(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="">Any experience</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10-15">10-15 years</option>
                  <option value="15+">15+ years</option>
                </select>
              </div>

              {/* Gender Filter */}
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Gender</label>
                <select 
                  value={selectedGender} 
                  onChange={(e) => setSelectedGender(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="">Any</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              {/* Language Filter */}
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Language</label>
                <select 
                  value={selectedLanguage} 
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="">Any language</option>
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="Mandarin">Mandarin</option>
                  <option value="Hindi">Hindi</option>
                  <option value="French">French</option>
                  <option value="Arabic">Arabic</option>
                </select>
              </div>

              {/* Insurance Filter */}
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Insurance</label>
                <select 
                  value={selectedInsurance} 
                  onChange={(e) => setSelectedInsurance(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="">All insurance</option>
                  <option value="BlueCross">BlueCross</option>
                  <option value="Aetna">Aetna</option>
                  <option value="United Healthcare">United Healthcare</option>
                  <option value="Cigna">Cigna</option>
                  <option value="Kaiser">Kaiser</option>
                  <option value="Medicare">Medicare</option>
                </select>
              </div>
            </div>

            {/* Results Section */}
            <div style={responsiveStyles.resultsSection}>
              <div style={responsiveStyles.resultsHeader}>
                <h3 style={responsiveStyles.resultsTitle}>
                  {filteredDoctors.length} Doctors Found
                </h3>
                {(searchQuery || selectedSpecialty || selectedLocation) && (
                  <p style={responsiveStyles.resultsSubtitle}>
                    {searchQuery && `Searching for "${searchQuery}"`}
                    {selectedSpecialty && ` in ${selectedSpecialty}`}
                    {selectedLocation && ` near ${selectedLocation}`}
                  </p>
                )}
              </div>

              <div style={responsiveStyles.doctorsGrid}>
                {filteredDoctors.map(doctor => (
                  <DoctorCard key={doctor.id} doctor={doctor} />
                ))}
              </div>

              {filteredDoctors.length === 0 && (
                <div style={responsiveStyles.noResults}>
                  <Search size={isSmallMobile ? 40 : 48} color="#CBD5E1" />
                  <h3 style={responsiveStyles.noResultsTitle}>No doctors found</h3>
                  <p style={responsiveStyles.noResultsText}>Try adjusting your filters or search criteria</p>
                  <button onClick={clearFilters} style={responsiveStyles.clearFiltersBtn}>
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const getResponsiveStyles = (isMobile, isSmallMobile) => ({
  pageWrapper: {
    minHeight: '100vh',
    backgroundColor: '#FFFFFF'
  },
  hero: {
    background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 50%, #1D57D5 100%)',
    paddingTop: isSmallMobile ? '130px' : isMobile ? '140px' : '150px',
    paddingBottom: isSmallMobile ? '40px' : isMobile ? '50px' : '80px',
    paddingLeft: isSmallMobile ? '16px' : isMobile ? '20px' : '32px',
    paddingRight: isSmallMobile ? '16px' : isMobile ? '20px' : '32px',
    position: 'relative',
    overflow: 'hidden',
    marginTop: '40px',
  },
  heroContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    textAlign: 'center',
    position: 'relative',
    zIndex: 1
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: isSmallMobile ? '6px 16px' : '8px 20px',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    borderRadius: '50px',
    color: '#FFFFFF',
    fontSize: isSmallMobile ? '12px' : '14px',
    fontWeight: '600',
    marginBottom: isSmallMobile ? '16px' : '24px'
  },
  heroTitle: {
    fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '56px',
    fontWeight: '800',
    color: '#FFFFFF',
    marginBottom: isSmallMobile ? '16px' : '20px',
    lineHeight: '1.2',
    padding: isSmallMobile ? '0 8px' : '0'
  },
  gradientText: {
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  heroSubtitle: {
    fontSize: isSmallMobile ? '14px' : isMobile ? '16px' : '20px',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: isSmallMobile ? '24px' : isMobile ? '32px' : '40px',
    lineHeight: '1.6',
    padding: isSmallMobile ? '0 8px' : '0'
  },
  searchContainer: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isSmallMobile ? '10px' : '12px',
    maxWidth: '800px',
    margin: '0 auto',
    marginBottom: isSmallMobile ? '24px' : isMobile ? '32px' : '40px',
    padding: isSmallMobile ? '0 8px' : '0',
    width: '100%'
  },
  searchBox: {
    flex: '1',
    width: isMobile ? '100%' : 'auto',
    minWidth: isMobile ? 'auto' : '300px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: isSmallMobile ? '14px 20px' : '16px 24px',
    background: '#FFFFFF',
    borderRadius: '50px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
  },
  searchInput: {
    flex: '1',
    border: 'none',
    outline: 'none',
    fontSize: isSmallMobile ? '14px' : '16px',
    color: '#1A202C',
    background: 'transparent'
  },
  filterToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: isSmallMobile ? '14px 24px' : '16px 32px',
    width: isMobile ? '100%' : 'auto',
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50px',
    color: '#FFFFFF',
    fontSize: isSmallMobile ? '14px' : '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  statsRow: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isMobile ? '20px' : '32px',
    padding: isSmallMobile ? '24px 16px' : isMobile ? '28px 20px' : '32px',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    maxWidth: '600px',
    margin: '0 auto',
    width: '100%'
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  statNumber: {
    fontSize: isSmallMobile ? '24px' : isMobile ? '28px' : '32px',
    fontWeight: '800',
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  statLabel: {
    fontSize: isSmallMobile ? '12px' : '14px',
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: '4px'
  },
  statDivider: {
    width: isMobile ? '100%' : '1px',
    height: isMobile ? '1px' : '40px',
    background: 'rgba(255, 255, 255, 0.2)',
    display: isMobile ? 'block' : 'block'
  },
  sectionWrapper: {
    padding: isSmallMobile ? '40px 16px' : isMobile ? '60px 20px' : '80px 32px',
    background: '#FFFFFF'
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: isSmallMobile ? '0' : '0'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: isSmallMobile ? '8px' : '12px',
    marginBottom: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
  },
  sectionTitle: {
    fontSize: isSmallMobile ? '24px' : isMobile ? '28px' : '36px',
    fontWeight: '800',
    color: '#1A202C'
  },
  mainSection: {
    padding: isSmallMobile ? '40px 16px' : isMobile ? '60px 20px' : '80px 32px',
    background: '#F8FAFC'
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '320px 1fr',
    gap: isMobile ? '0' : '40px'
  },
  filtersSidebar: {
    background: '#FFFFFF',
    padding: isSmallMobile ? '24px' : '32px',
    borderRadius: '20px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    height: 'fit-content',
    position: isMobile ? 'fixed' : 'sticky',
    top: isMobile ? '0' : '100px',
    left: isMobile ? '0' : 'auto',
    right: isMobile ? '0' : 'auto',
    bottom: isMobile ? '0' : 'auto',
    zIndex: isMobile ? 1000 : 'auto',
    overflowY: isMobile ? 'auto' : 'visible',
    maxHeight: isMobile ? '100vh' : 'none'
  },
  filtersSidebarMobile: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    borderRadius: '0',
    zIndex: 1000,
    overflowY: 'auto'
  },
  backdrop: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
    backdropFilter: 'blur(4px)'
  },
  mobileFilterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #E2E8F0'
  },
  closeFilterBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: '#64748B',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  filtersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #E2E8F0'
  },
  filtersTitle: {
    fontSize: isSmallMobile ? '18px' : '20px',
    fontWeight: '700',
    color: '#1A202C'
  },
  clearBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    color: '#64748B',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  filterGroup: {
    marginBottom: '24px'
  },
  filterLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A202C',
    marginBottom: '8px'
  },
  filterSelect: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #E2E8F0',
    borderRadius: '12px',
    fontSize: '14px',
    color: '#1A202C',
    background: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
  },
  filterInput: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #E2E8F0',
    borderRadius: '12px',
    fontSize: '14px',
    color: '#1A202C',
    background: '#FFFFFF',
    outline: 'none',
    transition: 'all 0.2s ease'
  },
  resultsSection: {
    minHeight: isMobile ? '400px' : '600px'
  },
  resultsHeader: {
    marginBottom: '32px'
  },
  resultsTitle: {
    fontSize: isSmallMobile ? '20px' : isMobile ? '24px' : '28px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '8px'
  },
  resultsSubtitle: {
    fontSize: isSmallMobile ? '14px' : '16px',
    color: '#64748B'
  },
  doctorsGrid: {
    display: 'grid',
    gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: isSmallMobile ? '16px' : isMobile ? '20px' : '24px'
  },
  doctorCard: {
    background: '#FFFFFF',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative'
  },
  featuredBadge: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
    borderRadius: '50px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#1A202C',
    zIndex: 1,
    boxShadow: '0 4px 12px rgba(244, 206, 123, 0.3)'
  },
  cardHeader: {
    position: 'relative',
    height: '280px',
    background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)'
  },
  doctorPhoto: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  ratingBadge: {
    position: 'absolute',
    bottom: '16px',
    left: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '700',
    color: '#1A202C',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
  },
  cardContent: {
    padding: '24px'
  },
  doctorName: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '4px'
  },
  specialty: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1D57D5',
    marginBottom: '8px'
  },
  qualifications: {
    fontSize: '13px',
    color: '#64748B',
    marginBottom: '16px',
    lineHeight: '1.5'
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px'
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#64748B'
  },
  availabilityRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #E2E8F0'
  },
  nextAvailable: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#28C08A',
    fontWeight: '600'
  },
  videoTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    background: '#E3EDFF',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#1D57D5',
    fontWeight: '600'
  },
  bookButton: {
    width: '100%',
    padding: '14px',
    marginTop: '16px',
    background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#FFFFFF',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  noResults: {
    textAlign: 'center',
    padding: isSmallMobile ? '60px 16px' : isMobile ? '70px 24px' : '80px 32px'
  },
  noResultsTitle: {
    fontSize: isSmallMobile ? '20px' : isMobile ? '22px' : '24px',
    fontWeight: '700',
    color: '#1A202C',
    marginTop: '20px',
    marginBottom: '8px'
  },
  noResultsText: {
    fontSize: isSmallMobile ? '14px' : '16px',
    color: '#64748B',
    marginBottom: '24px'
  },
  clearFiltersBtn: {
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 100%)',
    border: 'none',
    borderRadius: '50px',
    color: '#FFFFFF',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer'
  }
});

const styles = {
  doctorCard: {
    background: '#FFFFFF',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative'
  },
  featuredBadge: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
    borderRadius: '50px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#1A202C',
    zIndex: 1,
    boxShadow: '0 4px 12px rgba(244, 206, 123, 0.3)'
  },
  cardHeader: {
    position: 'relative',
    height: '280px',
    background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)'
  },
  doctorPhoto: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  ratingBadge: {
    position: 'absolute',
    bottom: '16px',
    left: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '700',
    color: '#1A202C',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
  },
  cardContent: {
    padding: '24px'
  },
  doctorName: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '4px'
  },
  specialty: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1D57D5',
    marginBottom: '8px'
  },
  qualifications: {
    fontSize: '13px',
    color: '#64748B',
    marginBottom: '16px',
    lineHeight: '1.5'
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px'
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#64748B'
  },
  availabilityRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #E2E8F0'
  },
  nextAvailable: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#28C08A',
    fontWeight: '600'
  },
  videoTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    background: '#E3EDFF',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#1D57D5',
    fontWeight: '600'
  },
  bookButton: {
    width: '100%',
    padding: '14px',
    marginTop: '16px',
    background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#FFFFFF',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  filterGroup: {
    marginBottom: '24px'
  },
  filterLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A202C',
    marginBottom: '8px'
  },
  filterSelect: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #E2E8F0',
    borderRadius: '12px',
    fontSize: '14px',
    color: '#1A202C',
    background: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
  },
  filterInput: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #E2E8F0',
    borderRadius: '12px',
    fontSize: '14px',
    color: '#1A202C',
    background: '#FFFFFF',
    outline: 'none',
    transition: 'all 0.2s ease'
  }
};

export default FindDoctors;
